<template>
  <ConfigPageShell
    title="Define Publish Rules"
    subtitle="Define when vehicles should be excluded from your website. Vehicles matching any rule below will be blocked from display."
  >
    <template #actions>
      <v-tooltip location="bottom" text="Create a new condition for blocking vehicles.">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-plus"
            @click="addRuleGroup"
          >
            Add Rule
          </v-btn>
        </template>
      </v-tooltip>
      <v-btn
        color="primary"
        variant="flat"
        data-testid="onboarding-listing-next"
        @click="handleOnboardingNext"
        v-if="isOnBoarding && !mdAndDown"
        :loading="listingStore.loadingStatus.config === 'loading'"
      >
        Next
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        @click="handleSave"
        v-else-if="!mdAndDown"
        :loading="listingStore.loadingStatus.config === 'loading'"
        :disabled="!hasUnsavedChanges"
        data-testid="save-btn"
      >
        Save
      </v-btn>
    </template>

    <template #belowHeader>
        <div class="text-caption text-grey-darken-1 mb-6" style="font-style: italic">
          💡 Tip: Keep rules simple — focus on VIN, make, model, year, and pricing validity.
        </div>
    </template>

        <v-card variant="outlined" class="mb-4" v-if="activeSegmentConfig">
          <v-card-text>
            <div class="mb-4 d-flex justify-space-between align-center">
              <p class="font-weight-bold mb-1">Publish Rules</p>
              <p class="text-caption text-grey">
                Vehicles matching these conditions will be archived and not appear on your website.
              </p>
            </div>

            <v-alert
              v-if="visibleRuleGroups.length === 0"
              type="info"
              variant="tonal"
              class="mb-4"
              density="compact"
            >
              No manual publish rules yet. Use <strong>Add Rule</strong> to create a
              dealer-authored rule.
            </v-alert>

            <!-- Rule Groups - Collapsed/Expanded View -->
            <div
              v-for="(item, displayIndex) in visibleRuleGroups"
              :key="item.originalIndex"
              class="mb-2"
            >
              <!-- Collapsed Summary View -->
              <v-card
                v-if="editingGroupIndex !== item.originalIndex"
                variant="outlined"
                class="rule-summary-card"
                data-testid="rule-summary-card"
                :class="{
                  'rule-warning': isRuleEmpty(item.group),
                  'rule-modified': isRuleGroupModified(item.originalIndex),
                }"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center flex-grow-1">
                      <span class="text-subtitle-2 text-grey-darken-1 me-3 font-weight-bold"
                        >#{{ displayIndex + 1 }}</span
                      >
                      <div class="flex-grow-1">
                        <span
                          class="text-body-1"
                          :class="{ 'text-warning': isRuleEmpty(item.group) }"
                        >
                          {{ generateRuleSummary(item.group) }}
                        </span>
                        <div v-if="item.group.reason" class="text-caption text-grey mt-1">
                          Reason: {{ item.group.reason }}
                        </div>
                        <div v-if="isRuleEmpty(item.group)" class="text-caption text-warning mt-1">
                          <v-icon size="x-small" class="me-1">mdi-alert</v-icon>
                          This rule will be removed when you save
                        </div>
                      </div>
                    </div>
                    <div class="d-flex align-center ga-1 ms-4">
                      <v-btn
                        icon
                        variant="text"
                        size="small"
                        @click="editGroup(item.originalIndex)"
                      >
                        <v-icon size="small">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        variant="text"
                        size="small"
                        class="delete-btn"
                        data-testid="rule-delete-btn"
                        @click="removeRuleGroup(item)"
                      >
                        <v-icon size="small">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Expanded Edit View -->
              <v-card v-else variant="outlined" class="mb-2">
                <v-card-text>
                  <div class="d-flex justify-space-between align-center mb-4">
                    <div>
                      <p class="text-body-1 mb-2">
                        Block vehicles that match:
                        <v-btn-toggle
                          v-model="item.group.logic"
                          mandatory
                          density="compact"
                          variant="outlined"
                          color="primary"
                          class="ms-2"
                          style="display: inline-flex; vertical-align: middle"
                        >
                          <v-btn value="OR" size="small">Match any condition</v-btn>
                          <v-btn value="AND" size="small">Match all conditions</v-btn>
                        </v-btn-toggle>
                      </p>
                    </div>
                    <div>
                      <v-btn variant="text" size="small" @click="cancelEdit" class="me-2">
                        Cancel
                      </v-btn>
                      <v-btn variant="flat" size="small" color="primary" @click="saveEdit">
                        Update
                      </v-btn>
                    </div>
                  </div>

                  <!-- Reason -->
                  <v-text-field
                    v-model="item.group.reason"
                    label="Reason (optional)"
                    placeholder="e.g. EV NOT IN FDDS1"
                    variant="outlined"
                    density="compact"
                    hide-details
                    bg-color="white"
                    class="mb-4"
                    style="max-width: 400px"
                  />

                  <!-- Rules within group -->
                  <div class="mb-3">
                    <p class="text-subtitle-2 font-weight-medium mb-3">Conditions</p>
                    <div
                      v-for="(rule, ruleIndex) in item.group.rules || []"
                      :key="ruleIndex"
                      class="mb-2"
                    >
                      <!-- Skip nested groups (we don't support them) -->
                      <v-row v-if="rule.type !== 'group'" dense align="center">
                        <!-- Field -->
                        <v-col cols="3">
                          <v-select
                            v-model="rule.field"
                            :items="fieldOptions"
                            item-title="title"
                            item-value="value"
                            placeholder="Field"
                            variant="outlined"
                            density="compact"
                            hide-details
                            bg-color="white"
                          ></v-select>
                        </v-col>

                        <!-- Operator -->
                        <v-col cols="4">
                          <v-select
                            v-model="rule.operator"
                            :items="operatorOptions"
                            placeholder="Operator"
                            variant="outlined"
                            density="compact"
                            hide-details
                            bg-color="white"
                          ></v-select>
                        </v-col>

                        <!-- Value -->
                        <v-col cols="4">
                          <v-text-field
                            v-model="rule.value"
                            :placeholder="getValuePlaceholder(rule.operator)"
                            variant="outlined"
                            density="compact"
                            hide-details
                            bg-color="white"
                          ></v-text-field>
                        </v-col>

                        <!-- Remove rule -->
                        <v-col cols="1">
                          <v-btn
                            icon
                            variant="text"
                            size="small"
                            class="delete-btn"
                            @click="removeRule(item.originalIndex, ruleIndex)"
                          >
                            <v-icon size="small">mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Add rule to group -->
                    <v-btn
                      variant="text"
                      size="small"
                      color="primary"
                      prepend-icon="mdi-plus"
                      @click="addRuleToGroup(item.originalIndex)"
                    >
                      Add Another Condition
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
    </ConfigPageShell>

    <!-- Floating action button (phones only) -->
    <v-fab-transition>
      <v-btn
        v-if="mdAndDown && (isOnBoarding || hasUnsavedChanges)"
        class="save-fab"
        color="primary"
        variant="elevated"
        size="large"
        :loading="listingStore.loadingStatus.config === 'loading'"
        @click="isOnBoarding ? handleOnboardingNext() : handleSave()"
      >
        <v-icon start>{{ isOnBoarding ? 'mdi-arrow-right' : 'mdi-content-save' }}</v-icon>
        {{ isOnBoarding ? 'Next' : 'Save' }}
      </v-btn>
    </v-fab-transition>
</template>

<script lang="ts" setup>
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import { onMounted, ref, computed } from 'vue'
import { useDisplay } from 'vuetify'
import { useListingConfigStore } from '@/stores/listingConfig'
import { makeAlert } from '@/utils'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { usePublishStore } from '@/stores/usePublishStore'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import type { LRGroup } from '@/types/appTypes'
import { useRuleConditionFieldsStore } from '@/stores/ruleConditionFields'
import {
  withLegacyUsedFields,
  type RuleConditionFieldOption,
} from '@/utils/ruleConditionFields'
import { cancelRuleGroupEdit, snapshotRuleGroup, type ListingRulesEditSnapshot } from '@/utils/listingRulesEditor'
import { isFeedRoutingGeneratedRule } from '@/utils/listingRules'

const listingStore = useListingConfigStore()
const accountSettings = useAccountSettingsStore()
const ruleFieldsStore = useRuleConditionFieldsStore()
const productSettings = useProductSettingsStore()
const appConfig = useJazelAppConfig()
const publishStore = usePublishStore()
const { smAndDown, mdAndDown } = useDisplay()

const props = defineProps<{ isOnBoarding?: boolean }>()
const emit = defineEmits<{ done: [] }>()

// Always use 'all' segment for global rules
const activeSegment = 'all'
const editingGroupIndex = ref<number | null>(null)

// Cancel should discard changes. Because the edit UI mutates the live config via v-model,
// we snapshot the group on entry and restore it on cancel. For newly-added groups, cancel
// removes the draft group entirely.
const editSnapshot = ref<ListingRulesEditSnapshot>(null)
const isNewGroupDraft = ref(false)

// Publish Rule condition fields (server-owned rule-condition catalog, not
// inferred from saved rules).
const fieldOptions = ref<RuleConditionFieldOption[]>([])

// Logic options for rule groups and connectors
const logicOptions = [
  { title: 'AND', value: 'AND' },
  { title: 'OR', value: 'OR' },
]

// Operator options for rule conditions
const operatorOptions = [
  { title: 'is equal to', value: 'equals' },
  { title: 'is equal to (case insensitive)', value: 'equals_ci' },
  { title: 'contains', value: 'contains' },
  { title: 'not contains', value: 'not_contains' },
  { title: 'is missing', value: 'missing' },
  { title: 'is not equal to', value: 'not_equals' },
  { title: 'is greater than', value: 'gt' },
  { title: 'is less than', value: 'lt' },
  { title: 'has length not equal to', value: 'length_not_in' },
]

// Get placeholder text for value input based on operator
const getValuePlaceholder = (operator: string): string => {
  switch (operator) {
    case 'length_not_in':
      return '17'
    case 'gt':
    case 'lt':
      return 'Enter number (e.g., 100)'
    case 'equals':
    case 'equals_ci':
    case 'not_equals':
      return 'Enter value'
    case 'contains':
      return 'Enter text to match'
    case 'missing':
      return 'true'
    default:
      return 'Value'
  }
}

// Generate human-readable summary from rule group
const generateRuleSummary = (group: any): string => {
  if (!group.rules || group.rules.length === 0) {
    return 'Block vehicles (no conditions set)'
  }

  // Filter out rules with empty fields or invalid values
  const validRules = group.rules.filter((rule: any) => {
    // Skip if field is empty
    if (!rule.field || rule.field.trim() === '') {
      return false
    }

    // For operators that require a value, check if value exists
    const operatorsRequiringValue = [
      'equals',
      'equals_ci',
      'not_equals',
      'contains',
      'gt',
      'lt',
      'length_not_in',
    ]
    if (operatorsRequiringValue.includes(rule.operator)) {
      // Skip if value is empty, null, or undefined
      if (rule.value === null || rule.value === undefined || rule.value === '') {
        return false
      }
      // Skip if value is an empty array
      if (Array.isArray(rule.value) && rule.value.length === 0) {
        return false
      }
    }

    return true
  })

  // If no valid rules after filtering
  if (validRules.length === 0) {
    return 'Block vehicles (no conditions set)'
  }

  const logic = group.logic || 'OR'

  // Group rules by operator for more natural language
  const groupedByOperator: Record<string, any[]> = {}
  validRules.forEach((rule: any) => {
    const key = `${rule.operator}|${rule.value}`
    if (!groupedByOperator[key]) {
      groupedByOperator[key] = []
    }
    groupedByOperator[key].push(rule)
  })

  // Build descriptions for each group
  const descriptions: string[] = []

  for (const [key, rulesGroup] of Object.entries(groupedByOperator)) {
    const [operator, value] = key.split('|')
    const fields = rulesGroup.map((r) => r.field)

    descriptions.push(formatGroupedRuleDescription(fields, operator, value))
  }

  // Join descriptions with appropriate connector
  const connector = logic === 'AND' ? 'and' : 'or'
  return `Block vehicles with ${descriptions.join(` ${connector} `)}`
}

const formatGroupedRuleDescription = (
  fields: string[],
  operator: string,
  value: string,
): string => {
  const fieldList = formatFieldList(fields)

  // Format based on operator type with natural language
  switch (operator) {
    case 'missing':
      return `missing ${fieldList}`

    case 'equals':
    case 'equals_ci':
      if (fields.length === 1) {
        return `${fields[0]} equal to "${value}"`
      }
      return `${fieldList} equal to "${value}"`

    case 'not_equals':
      if (fields.length === 1) {
        return `${fields[0]} not equal to "${value}"`
      }
      return `${fieldList} not equal to "${value}"`

    case 'contains':
      if (fields.length === 1) {
        return `${fields[0]} containing "${value}"`
      }
      return `${fieldList} containing "${value}"`

    case 'gt':
      if (fields.length === 1) {
        return `${fields[0]} greater than ${value}`
      }
      return `${fieldList} greater than ${value}`

    case 'lt':
      if (fields.length === 1) {
        return `${fields[0]} less than ${value}`
      }
      return `${fieldList} less than ${value}`

    case 'length_not_in':
      const parsedValue = value.startsWith('[') ? JSON.parse(value) : value
      const lengthValue = Array.isArray(parsedValue) ? parsedValue.join(' or ') : parsedValue
      if (fields.length === 1) {
        return `${fields[0]} not ${lengthValue} characters long`
      }
      return `${fieldList} not ${lengthValue} characters long`

    default:
      // Fallback for unknown operators
      if (value === 'true' || value === 'false') {
        return `${fieldList} ${operator}`
      }
      return `${fieldList} ${operator} "${value}"`
  }
}

const formatFieldList = (fields: string[]): string => {
  if (fields.length === 1) {
    return fields[0]
  }

  if (fields.length === 2) {
    return `${fields[0]} and ${fields[1]}`
  }

  // 3 or more fields: "field1, field2, and field3"
  const allButLast = fields.slice(0, -1).join(', ')
  const last = fields[fields.length - 1]
  return `${allButLast}, and ${last}`
}

// Check if a rule group has no valid conditions
const isRuleEmpty = (group: any): boolean => {
  if (!group.rules || group.rules.length === 0) {
    return true
  }

  // Check if all rules are invalid (empty fields or values)
  const hasValidRule = group.rules.some((rule: any) => {
    // Rule is valid if it has a field name
    if (!rule.field || rule.field.trim() === '') {
      return false
    }

    // For operators that require a value, check if value exists
    const operatorsRequiringValue = [
      'equals',
      'equals_ci',
      'not_equals',
      'contains',
      'gt',
      'lt',
      'length_not_in',
    ]
    if (operatorsRequiringValue.includes(rule.operator)) {
      if (rule.value === null || rule.value === undefined || rule.value === '') {
        return false
      }
      if (Array.isArray(rule.value) && rule.value.length === 0) {
        return false
      }
    }

    return true
  })

  return !hasValidRule
}

// Edit mode functions
const editGroup = (index: number) => {
  const rules = activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules
  editSnapshot.value = snapshotRuleGroup(rules, index)
  isNewGroupDraft.value = false
  editingGroupIndex.value = index
}

const cancelEdit = () => {
  const rules = activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules
  const next = cancelRuleGroupEdit({
    rules,
    editingIndex: editingGroupIndex.value,
    isNewGroupDraft: isNewGroupDraft.value,
    snapshot: editSnapshot.value,
  })

  editingGroupIndex.value = next.editingIndex
  editSnapshot.value = next.snapshot
  isNewGroupDraft.value = next.isNewGroupDraft
}

const saveEdit = () => {
  // Clean up the current group being edited
  if (
    editingGroupIndex.value !== null &&
    activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules
  ) {
    const group = activeSegmentConfig.value.inventory_membership_rules.ruleset.rules[editingGroupIndex.value]

    if (group?.type === 'group' && group.rules && Array.isArray(group.rules)) {
      // Filter out invalid rules
      group.rules = group.rules.filter((rule: any) => {
        // Keep rule if it has a field name
        if (!rule.field || rule.field.trim() === '') {
          return false
        }

        // For operators that require a value, check if value exists
        const operatorsRequiringValue = [
          'equals',
          'equals_ci',
          'not_equals',
          'contains',
          'gt',
          'lt',
          'length_not_in',
        ]
        if (operatorsRequiringValue.includes(rule.operator)) {
          // Remove if value is empty, null, or undefined
          if (rule.value === null || rule.value === undefined || rule.value === '') {
            return false
          }
          // Remove if value is an empty array
          if (Array.isArray(rule.value) && rule.value.length === 0) {
            return false
          }
        }

        return true
      })

      // If no valid rules left, ensure at least one empty rule for editing
      if (group.rules.length === 0) {
        group.rules.push({
          type: 'rule',
          field: '',
          operator: 'equals',
          value: '',
        })
      }
    }
  }

  editingGroupIndex.value = null
  editSnapshot.value = null
  isNewGroupDraft.value = false
}

// Active segment config - always use 'all' for global rules
const activeSegmentConfig = computed(() => {
  if (!listingStore.listingConfig) return null

  // Ensure 'all' segment exists in config
  if (!listingStore.listingConfig[activeSegment]) {
    listingStore.listingConfig[activeSegment] = {
      inventory_membership_rules: {
        ruleset: {
          logic: 'AND',
          rules: [],
        },
        evaluation: {
          mode: 'first_match',
          fallback_action: 'allow',
        },
      },
    }
  }

  return listingStore.listingConfig[activeSegment]
})

// Type for visible rule groups in the template
type VisibleRuleGroup = {
  group: LRGroup
  originalIndex: number
  isStandaloneRule?: boolean
}

// Type predicate to filter out nulls
function isVisibleRuleGroup(item: VisibleRuleGroup | null): item is VisibleRuleGroup {
  return item !== null
}

const visibleRuleGroups = computed((): VisibleRuleGroup[] => {
  const allRules = activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules || []

  return allRules
    .map((item: any, originalIndex: number): VisibleRuleGroup | null => {
      if (!item) return null

      if (isFeedRoutingGeneratedRule(item)) return null

      if (item.type === 'rule') {
        return {
          group: {
            type: 'group',
            logic: 'OR',
            rules: [item],
            action: item.action || 'block',
            reason: item.reason || '',
          },
          originalIndex,
          isStandaloneRule: true,
        }
      }

      if (!item.rules || item.rules.length === 0) {
        return null
      }

      const hasValidRules = item.rules.some((rule: any) => {
        if (rule && rule.type === 'group') {
          return false
        }
        return rule && rule.field && rule.field.trim() !== ''
      })

      if (
        hasValidRules ||
        item.rules.length === 0 ||
        item.rules.every((r: any) => !r.field || r.field.trim() === '')
      ) {
        return {
          group: item,
          originalIndex,
        }
      }

      return null
    })
    .filter(isVisibleRuleGroup)
})

// Track original config for dirty state detection
const originalConfig = ref<any>(null)

// Normalize an object for comparison (remove Vue reactivity and sort keys)
const normalizeForComparison = (obj: any): string => {
  // Create a deep copy to remove Vue reactivity
  const cleaned = JSON.parse(JSON.stringify(obj))

  // Sort object keys recursively for consistent comparison
  const sortObject = (o: any): any => {
    if (Array.isArray(o)) {
      return o.map(sortObject)
    }
    if (o !== null && typeof o === 'object') {
      return Object.keys(o)
        .sort()
        .reduce((result: any, key) => {
          result[key] = sortObject(o[key])
          return result
        }, {})
    }
    return o
  }

  return JSON.stringify(sortObject(cleaned))
}

// Check if there are unsaved changes
const hasUnsavedChanges = computed(() => {
  if (!originalConfig.value || !listingStore.listingConfig) {
    return false
  }

  try {
    // Compare current config with original using normalized JSON
    const currentStr = normalizeForComparison(listingStore.listingConfig)
    const originalStr = normalizeForComparison(originalConfig.value)

    return currentStr !== originalStr
  } catch (e) {
    console.error('Error comparing configs:', e)
    return false
  }
})

// Check if a specific rule group has been modified
const isRuleGroupModified = (groupIndex: number): boolean => {
  if (!originalConfig.value || !listingStore.listingConfig) {
    return false
  }

  const currentGroup = activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules?.[groupIndex]
  const originalGroup = originalConfig.value['all']?.inventory_membership_rules?.ruleset?.rules?.[groupIndex]

  if (!currentGroup && !originalGroup) return false
  if (!currentGroup || !originalGroup) return true

  try {
    // Compare the groups using normalized JSON
    const currentStr = normalizeForComparison(currentGroup)
    const originalStr = normalizeForComparison(originalGroup)

    return currentStr !== originalStr
  } catch (e) {
    console.error('Error comparing rule groups:', e)
    return false
  }
}

// Initialize: fetch listing config from JAMS
onMounted(async () => {
  await listingStore.getListingConfig()
  await loadRuleConditionFields()

  // Store a deep copy of the original config for comparison
  if (listingStore.listingConfig) {
    originalConfig.value = JSON.parse(JSON.stringify(listingStore.listingConfig))
  }
})

// Collect field names already referenced by saved rules. Used ONLY to keep
// existing selections from rendering blank (display-only legacy entries) — never
// as the source of which fields are available.
const collectUsedFields = (): string[] => {
  const out = new Set<string>()
  const rules: any[] = (listingStore.listingConfig as any)?.all?.inventory_membership_rules?.ruleset?.rules || []
  for (const item of rules) {
    if (item?.type === 'rule') {
      const f = String(item?.field || '').trim()
      if (f) out.add(f)
      continue
    }
    if (item?.type === 'group' && Array.isArray(item.rules)) {
      for (const r of item.rules) {
        if (r?.type !== 'rule') continue
        const f = String(r?.field || '').trim()
        if (f) out.add(f)
      }
    }
  }
  return Array.from(out)
}

// Load the deliberate, server-owned rule-condition field catalog (via the
// shared store). Availability is intentional and account-independent: it does
// NOT depend on the dealer's configured feeds or on which fields appear in
// already-saved rules.
const loadRuleConditionFields = async () => {
  await ruleFieldsStore.load()
  const catalog: RuleConditionFieldOption[] = ruleFieldsStore.options
  fieldOptions.value = withLegacyUsedFields(catalog, collectUsedFields())
}

// Add a new rule group
const addRuleGroup = () => {
  if (!activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules) return

  // Add the new rule group
  activeSegmentConfig.value.inventory_membership_rules.ruleset.rules.push({
    type: 'group',
    logic: 'OR',
    rules: [
      {
        type: 'rule',
        field: '',
        operator: 'equals',
        value: '',
      },
    ],
    action: 'block',
    reason: '',
  })

  // Automatically expand the newly created rule for editing
  const newIndex = activeSegmentConfig.value.inventory_membership_rules.ruleset.rules.length - 1
  isNewGroupDraft.value = true
  editSnapshot.value = null
  editingGroupIndex.value = newIndex
}

// Remove a rule group
const removeRuleGroup = (item: any) => {
  if (!activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules) return

  const rules = activeSegmentConfig.value.inventory_membership_rules.ruleset.rules

  // For standalone rules (wrapped for display), we need to find and remove the actual standalone rule
  if (item.isStandaloneRule) {
    // Find the standalone rule by matching the VIN value
    const ruleToDelete = item.group.rules[0] // The wrapped rule
    const indexToDelete = rules.findIndex(
      (r: any) =>
        r.type === 'rule' && r.field === ruleToDelete.field && r.value === ruleToDelete.value,
    )
    if (indexToDelete !== -1) {
      console.log('🗑️ Removing standalone rule at index', indexToDelete, ':', ruleToDelete)
      rules.splice(indexToDelete, 1)
    }
  } else {
    // For regular groups, find by deep equality to avoid stale index issues
    const indexToDelete = rules.findIndex(
      (r: any) => r === item.group || JSON.stringify(r) === JSON.stringify(item.group),
    )
    if (indexToDelete !== -1) {
      console.log('🗑️ Removing group at index', indexToDelete)
      rules.splice(indexToDelete, 1)
    } else {
      console.warn('⚠️ Could not find group to delete, falling back to originalIndex')
      rules.splice(item.originalIndex, 1)
    }
  }
}

// Add a rule to a group
const addRuleToGroup = (groupIndex: number) => {
  const group = activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules?.[groupIndex]
  if (!group || group.type !== 'group' || !group.rules) return

  group.rules.push({
    type: 'rule',
    field: '',
    operator: 'equals',
    value: '',
  })
}

// Remove a rule from a group
const removeRule = (groupIndex: number, ruleIndex: number) => {
  const group = activeSegmentConfig.value?.inventory_membership_rules?.ruleset?.rules?.[groupIndex]
  if (!group || group.type !== 'group' || !group.rules) return

  // Don't allow removing the last rule in a group
  if (group.rules.length > 1) {
    group.rules.splice(ruleIndex, 1)
  } else {
    makeAlert('A group must have at least one rule', 'warning')
  }
}

// Clean up incomplete rules before saving
import { cleanupListingRules } from '@/utils/listingRules'
const cleanupRules = () => {
  if (!listingStore.listingConfig) return
  listingStore.listingConfig = cleanupListingRules(listingStore.listingConfig as any) as any
}

const handleSave = async () => {
  cleanupRules()

  if (!listingStore.listingConfig) {
    makeAlert('No publish rules configuration to save.', 'error')
    return false
  }

  const success = await listingStore.saveListingConfig(listingStore.listingConfig, false)

  if (success) {
    if (!props.isOnBoarding) {
      makeAlert('Publish rules saved as draft. Click "Publish Changes" to apply to live inventory.', 'success')
    }
    await listingStore.getListingConfig()

    try {
      const feedsEndpoint = import.meta.env.VITE_ISE_FEEDS_URL
      const accountId = accountSettings.accountId
      await publishStore.checkAndReconcile(feedsEndpoint, accountId)

      const { compareListingRules } = await import('@/utils/compareConfigs')
      const publishedConfig = productSettings.publishedSnapshot?.listingRules
      const comparison = compareListingRules(publishedConfig, listingStore.listingConfig)

      if (comparison.hasChanges) {
        const changes: any = {
          timestamp: new Date().toISOString(),
          ...comparison,
        }
        await publishStore.markDirty('inventoryMembership', changes)
      } else if (productSettings.unpublished?.sources?.inventoryMembership) {
        const sources = { ...(productSettings.unpublished.sources || {}) }
        delete sources.inventoryMembership
        if (Object.keys(sources).length === 0) {
          await publishStore.reset()
        }
      }
    } catch (error) {
      console.error('❌ Error during comparison/marking dirty:', error)
      makeAlert(
        'Saved successfully but failed to track changes. Please refresh the page.',
        'warning',
      )
    }

    originalConfig.value = JSON.parse(JSON.stringify(listingStore.listingConfig))
    return true
  }

  makeAlert('Error saving publish rules.', 'error')
  return false
}

const saveListingRulesIfNeeded = async (): Promise<boolean> => {
  if (!hasUnsavedChanges.value) return true
  return (await handleSave()) === true
}

const handleOnboardingNext = async () => {
  const saved = await saveListingRulesIfNeeded()
  if (!saved) return
  emit('done')
}
</script>

<style scoped>
.save-fab {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2000;
}

.max-width-300 {
  max-width: 300px;
  width: 300px;
}

.rule-summary-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.rule-summary-card:hover {
  background-color: #f9fafb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

.rule-warning {
  border-color: #fb8c00 !important;
  background-color: #fff3e0 !important;
}

.rule-warning:hover {
  border-color: #f57c00 !important;
  background-color: #ffe0b2 !important;
  box-shadow: 0 2px 4px rgba(251, 140, 0, 0.15) !important;
}

/* Modified rule - orange border to indicate unsaved changes */
.rule-modified {
  border-left: 3px solid #ff9800 !important;
  background-color: rgba(255, 152, 0, 0.04) !important;
}

.rule-modified:hover {
  background-color: rgba(255, 152, 0, 0.08) !important;
  box-shadow: 0 2px 4px rgba(255, 152, 0, 0.15) !important;
}

/* Delete button - neutral by default, red on hover (destructive action) */
.delete-btn {
  color: rgba(0, 0, 0, 0.6);
  transition: color 0.2s ease;
}

.delete-btn:hover {
  color: #d32f2f !important;
}
</style>
